import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dataApi } from '../../services/api';
import { emptyStatusCounts } from '../../lib/quoteRequestStatus';
import { toast } from 'sonner';

// shadcn UI components
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { StatTile } from '../../components/ui/stat-tile';
import { QuoteStatusBadge } from '../../components/ui/quote-status-badge';
import PageHeaderActions from '../../components/PageHeaderActions';
import { useObfuscate } from '../../hooks/useObfuscate';
import { useAuth } from '../../context/AuthContext';

// Sidebar icons
import { ReactComponent as QuoteRequestsIcon } from '../../assets/Icons/quote-requests.svg';
import { ReactComponent as PhonebookIcon } from '../../assets/Icons/phonebook.svg';
import { ReactComponent as FleetIcon } from '../../assets/Icons/fleet.svg';
import { Inbox, PencilLine, Send, CheckCircle2, ArrowRight, Link2 } from 'lucide-react';

/**
 * The quoting pipeline, in the order a request moves through it.
 *
 * One tile per status, and each one filters the Quotes list to exactly what it
 * counted — a number nobody can click through to is decoration. Declared once
 * so the loading skeletons and the real tiles cannot drift apart.
 */
const PIPELINE = [
  {
    status: 'new',
    icon: Inbox,
    label: 'New',
    hint: 'Waiting to be picked up',
    tone: 'info'
  },
  {
    status: 'drafting',
    icon: PencilLine,
    label: 'Drafting',
    hint: 'Quotes being written',
    tone: 'warning'
  },
  {
    status: 'awaiting response',
    icon: Send,
    label: 'Awaiting response',
    hint: 'Sent, waiting on the customer'
  },
  {
    status: 'accepted',
    icon: CheckCircle2,
    label: 'Accepted',
    hint: 'Approved to go ahead',
    tone: 'positive'
  }
];

// Deep links into the Quotes list. QuoteRequests reads ?status= on mount.
const quotesLink = (status) => `/quote-requests?status=${encodeURIComponent(status)}`;

const QUICK_ACTIONS = [
  {
    to: '/quote-requests',
    icon: QuoteRequestsIcon,
    title: 'Quote requests',
    description: 'Review new requests and build quotes'
  },
  {
    to: '/phonebook',
    icon: PhonebookIcon,
    title: 'Phonebook',
    description: 'Look up customers and their vehicles'
  },
  {
    to: '/fleet',
    icon: FleetIcon,
    title: 'Fleet',
    description: 'Manage courtesy cars and bookings'
  }
];

const greeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const EMPTY_STATS = {
  totalQuoteRequests: 0,
  byStatus: emptyStatusCounts(),
  recentRequests: []
};

const Dashboard = () => {
  const priv = useObfuscate();
  const { user } = useAuth();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  // Tracked separately from the counts, because zero and "we could not find
  // out" are different things and only one of them means a shop is new here.
  const [failed, setFailed] = useState(false);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const dashboardData = await dataApi.dashboardStats();
      setStats({
        totalQuoteRequests: dashboardData.totalQuoteRequests,
        byStatus: { ...emptyStatusCounts(), ...dashboardData.byStatus },
        recentRequests: dashboardData.recentRequests || []
      });
      setFailed(false);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
      setStats(EMPTY_STATS);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // A shop with no requests yet has nothing to count. Showing it four zeroes
  // and an empty list tells it only that the app is empty; it is standing at
  // the start of setup, so point it at the one thing that starts the pipeline.
  const isFirstRun = !loading && !failed && stats.totalQuoteRequests === 0;

  const subtitle = useMemo(() => {
    if (loading) return 'Loading your quoting pipeline…';
    if (failed) return 'Your quoting pipeline could not be loaded.';
    if (isFirstRun) return 'Let’s get your first quote request in.';

    const waiting = stats.byStatus.new + stats.byStatus.drafting;
    if (waiting === 0) return 'Nothing waiting on you — every request has been quoted.';
    return `${waiting} quote ${waiting === 1 ? 'request needs' : 'requests need'} your attention.`;
  }, [loading, failed, isFirstRun, stats.byStatus]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold">
            {greeting()}{user?.first_name ? `, ${priv.firstName(user.first_name)}` : ''}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <PageHeaderActions />
      </div>

      {/* Pipeline — one tile per status, each linking to that filter */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {PIPELINE.map((stage) => (
          <StatTile
            key={stage.status}
            icon={stage.icon}
            label={stage.label}
            hint={stage.hint}
            tone={stage.tone}
            to={quotesLink(stage.status)}
            value={loading || failed ? '—' : stats.byStatus[stage.status]}
            loading={loading}
          />
        ))}
      </div>

      {isFirstRun ? (
        <Card>
          <CardHeader>
            <CardTitle>Get your first quote request</CardTitle>
            <CardDescription>
              Customers send you photos and damage details through your own quote form. Share
              the link and requests land here.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/settings/general">
                <Link2 className="h-4 w-4" />
                Get your customer link
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/quote-requests">
                Add one yourself
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>Recent quote requests</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to="/quote-requests">View all</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 px-4 py-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-4 w-56" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            ) : failed ? (
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">
                  We could not reach your quote requests just now.
                </p>
                <Button variant="outline" size="sm" onClick={fetchDashboardStats}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recentRequests.map((request) => {
                  const name = priv.name(
                    `${request.customer?.firstName || request.firstName || ''} ${request.customer?.lastName || request.lastName || ''}`.trim()
                  );
                  const vehicle = [
                    request.vehicle?.year || request.year,
                    request.vehicle?.make || request.make,
                    request.vehicle?.model || request.model
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <Link
                      key={request.$id}
                      to={`/quote-requests/${request.$id}`}
                      className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 px-4 py-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="min-w-0">
                        <h4 className="truncate font-semibold">{name || 'Unnamed customer'}</h4>
                        <p className="truncate text-sm text-muted-foreground">
                          {vehicle || 'Vehicle not recorded'}
                          <span aria-hidden="true"> · </span>
                          {formatDate(request.$createdAt)}
                        </p>
                      </div>
                      <QuoteStatusBadge status={request.status} />
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {QUICK_ACTIONS.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="group/action flex items-center gap-4 rounded-xl bg-muted/50 p-5 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <action.icon aria-hidden="true" className="h-6 w-6 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <h3 className="font-semibold">{action.title}</h3>
                  <p className="text-sm text-muted-foreground">{action.description}</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/action:opacity-100" />
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
